import React from "react";
import {
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";

import PatientAutocomplete from "./PatientAutocomplete";
import { TASK_CATEGORIES, TASK_PRIORITIES } from "../../constants/clinicalDashboard";

/**
 * Formulario de creación/edición de tareas con react-hook-form.
 */
export default function TaskForm({ defaultValues, patients, employees, currentUser, onSubmit, onCancel }) {
  // Reconstruir patient desde campos planos si no viene como objeto
  const initialPatient = defaultValues?.patient
    || (defaultValues?.cipId
      ? { cipId: defaultValues.cipId, name: defaultValues.patientName }
      : null);

  // Buscar al currentUser en la lista de employees para pre-fill
  const currentEmployee = employees?.find(
    (e) => e.employeeId === currentUser?.employeeId
  ) || null;

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: defaultValues?.title || "",
      description: defaultValues?.description || "",
      category: defaultValues?.category || "clinical",
      priority: defaultValues?.priority || "medium",
      dueDate: defaultValues?.dueDate || new Date().toISOString().split("T")[0],
      patient: initialPatient,
      assignee: defaultValues?.assignee || currentEmployee,
      assignToMyself: !defaultValues?.assignee,
    },
  });

  const assignToMyself = watch("assignToMyself");

  const onFormSubmit = (data) => {
    const assignee = data.assignToMyself ? currentEmployee : data.assignee;
    onSubmit({
      title: data.title,
      description: data.description,
      category: data.category,
      priority: data.priority,
      dueDate: data.dueDate || null,
      cipId: data.patient?.cipId || defaultValues?.cipId || null,
      patientName: data.patient?.name || defaultValues?.patientName || null,
      assigneeId: assignee?.employeeId || null,
      assigneeName: assignee
        ? `${assignee.firstName} ${assignee.lastName}`
        : null,
    });
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <Stack spacing={2}>
        {/* Title */}
        <Controller
          name="title"
          control={control}
          rules={{ required: "Title is required", maxLength: { value: 500, message: "Max 500 characters" } }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Title"
              size="small"
              required
              error={!!errors.title}
              helperText={errors.title?.message}
              fullWidth
            />
          )}
        />

        {/* Description */}
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Description"
              size="small"
              multiline
              rows={2}
              fullWidth
            />
          )}
        />

        {/* Category + Priority row */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth>
                <InputLabel>Category</InputLabel>
                <Select {...field} label="Category">
                  {TASK_CATEGORIES.map((c) => (
                    <MenuItem key={c.key} value={c.key}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          <Controller
            name="priority"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select {...field} label="Priority">
                  {TASK_PRIORITIES.map((p) => (
                    <MenuItem key={p.key} value={p.key}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <span style={{ color: p.color, fontSize: 10 }}>&#9679;</span>
                        <span>{p.label}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Stack>

        {/* Due Date */}
        <Controller
          name="dueDate"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Due Date"
              type="date"
              size="small"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
          )}
        />

        {/* Patient Autocomplete */}
        <Controller
          name="patient"
          control={control}
          render={({ field }) => (
            <PatientAutocomplete
              patients={patients}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />

        {/* Assignee */}
        {employees?.length > 0 && (
          <Stack spacing={0.5}>
            <Controller
              name="assignToMyself"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        if (e.target.checked) {
                          setValue("assignee", currentEmployee);
                        }
                      }}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                      Assign to myself
                    </Typography>
                  }
                />
              )}
            />
            {!assignToMyself && (
              <Controller
                name="assignee"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={employees}
                    getOptionLabel={(opt) =>
                      opt ? `${opt.firstName} ${opt.lastName}` : ""
                    }
                    filterOptions={(options, { inputValue: q }) => {
                      if (!q) return options;
                      const lower = q.toLowerCase();
                      return options.filter(
                        (e) =>
                          e.firstName?.toLowerCase().includes(lower) ||
                          e.lastName?.toLowerCase().includes(lower) ||
                          `${e.firstName} ${e.lastName}`.toLowerCase().includes(lower) ||
                          e.jobPositionName?.toLowerCase().includes(lower)
                      );
                    }}
                    isOptionEqualToValue={(opt, val) =>
                      opt?.employeeId === val?.employeeId
                    }
                    value={field.value}
                    onChange={(_, newVal) => field.onChange(newVal)}
                    renderOption={(props, opt) => {
                      const { key, ...rest } = props;
                      return (
                        <li key={opt.employeeId} {...rest}>
                          <Stack>
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.8rem" }}>
                              {opt.firstName} {opt.lastName}
                            </Typography>
                            {opt.jobPositionName && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                                {opt.jobPositionName}
                              </Typography>
                            )}
                          </Stack>
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Assign to"
                        placeholder="Type name or position..."
                        size="small"
                      />
                    )}
                    size="small"
                  />
                )}
              />
            )}
          </Stack>
        )}

        {/* Actions */}
        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <Button
            variant="outlined"
            size="small"
            onClick={onCancel}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="small"
            sx={{ textTransform: "none" }}
          >
            Save Task
          </Button>
        </Stack>
      </Stack>
    </form>
  );
}
